import { CAPABILITY } from "./permissions";
import { actorCanAccessPlayStoreReadiness } from "./playStoreReadinessAccess";
import { actorCanAccessGoogleVisibility } from "./googleVisibilityAccess";
import { actorCanAccessFeedbackInbox } from "../feedback/feedbackInboxAccess";
import { canAccessMode } from "./screenGuards";
import { isGuestActor } from "./guestMode";
import { GIT_BRANCH_MAP_NAV_ITEM, isBranchMapPreviewHost } from "./branchMapAccess";

/**
 * Application desktop sidebar destinations.
 * Visibility uses existing Mode Select / screen-guard capabilities.
 * Do not add destinations that are not already reachable screens.
 */
export const APP_DESKTOP_NAV_ITEMS = Object.freeze([
  {
    id: "kpi_dashboard",
    label: "Dashboard",
    group: null,
    capability: CAPABILITY.SCREEN_KPI_DASHBOARD,
    icon: null,
  },
  {
    id: "create_project",
    label: "Projects",
    group: null,
    capability: CAPABILITY.SCREEN_CREATE_PROJECT,
    icon: "📁",
  },
  {
    id: "create_client",
    label: "Clients",
    group: "Workspaces",
    capability: CAPABILITY.SCREEN_CREATE_CLIENT,
    icon: "👤",
  },
  {
    id: "estimates",
    label: "Estimates",
    group: "Workspaces",
    capability: CAPABILITY.SCREEN_ESTIMATE_HUB,
    icon: "📋",
    mode: "estimates",
  },
  {
    id: "intelligence",
    label: "Reports & Analytics",
    group: "Reporting & Analytics",
    capability: CAPABILITY.SCREEN_INTELLIGENCE,
    icon: "📊",
    mode: "intelligence",
  },
  {
    id: "mission",
    label: "Mission Control",
    group: "Reporting & Analytics",
    capability: CAPABILITY.SCREEN_MISSION_CONTROL,
    icon: "🎯",
    mode: "mission",
  },
  {
    id: "finance",
    label: "Finance",
    group: "Finance",
    capability: CAPABILITY.SCREEN_FINANCE,
    icon: "🏦",
    mode: "finance",
  },
  {
    id: "materials",
    label: "Materials & Services",
    group: "Operations",
    capability: CAPABILITY.SCREEN_MATERIALS,
    icon: "📦",
    mode: "materials",
  },
  {
    id: "tasks",
    label: "Tasks",
    group: "Operations",
    capability: CAPABILITY.SCREEN_TASKS,
    icon: "✅",
    mode: "tasks",
  },
  {
    id: "planning",
    label: "Planning",
    group: "Operations",
    capability: CAPABILITY.SCREEN_PLANNING,
    icon: "🗓️",
    mode: "planning",
  },
  {
    id: "manual_time_entry",
    label: "Time Logs",
    group: "Operations",
    capability: CAPABILITY.SCREEN_MANUAL_TIME_ENTRY,
    icon: "⏱️",
  },
  {
    id: "google_visibility",
    label: "Google Visibility",
    group: "Internal Tools",
    surfaceRenaudOnly: true,
    icon: "🔎",
  },
  {
    id: "play_store_readiness",
    label: "Play Store Readiness",
    group: "Internal Tools",
    nexoraOnly: true,
    icon: "📡",
    // Retired legacy console: no sidebar entry. The id stays so the old
    // route resolves to the retired notice instead of a broken screen.
    sidebar: false,
  },
  {
    id: "feedback_inbox",
    label: "Feedback Inbox",
    group: "Internal Tools",
    icon: "📥",
    mode: "feedback_inbox",
  },
  {
    id: "feedback",
    label: "Send Feedback",
    group: "Settings",
    icon: "💬",
    authenticated: true,
  },
  {
    id: "settings",
    label: "Settings",
    group: "Settings",
    icon: "⚙️",
    // Same destination as the dashboard gear. Any signed-in actor can open it;
    // sections inside stay on the existing capability gates.
    authenticated: true,
  },
  {
    id: "crewpilot",
    label: "Time & Session",
    group: null,
    capability: CAPABILITY.SCREEN_COCKPIT,
    icon: "🧭",
    mode: "crewpilot",
    // Compatibility route only. Not a selectable sidebar destination.
    sidebar: false,
  },
]);

const APP_DESKTOP_SHELL_SCREENS = new Set([
  "kpi_dashboard",
  "create_client",
  "create_project",
  "project_detail",
  "manual_time_entry",
  "materials",
  "tasks",
  "planning",
  "crewpilot",
  "estimate_hub",
  "estimate_pipeline",
  "invoice_hub",
  "accounts_receivable",
  "accounts_payable",
  "finance_dashboard",
  "finance_accounts",
  "finance_transactions",
  "finance_money_in",
  "finance_money_out",
  "finance_transfer",
  "financial_position",
  "intelligence",
  "report_builder",
  "project_analytics",
  "payroll_home",
  "payroll_queue",
  "payroll_report",
  "mission_control",
  "user_preferences",
  "feedback",
  "feedback_inbox",
  "play_store_readiness",
  "google_visibility",
]);

const APP_DESKTOP_NAV_ACTIVE_BY_SCREEN = Object.freeze({
  kpi_dashboard: "kpi_dashboard",
  create_client: "create_client",
  create_project: "create_project",
  project_detail: "create_project",
  manual_time_entry: "manual_time_entry",
  materials: "materials",
  tasks: "tasks",
  planning: "planning",
  crewpilot: "crewpilot",
  estimate_hub: "estimates",
  estimate_pipeline: "estimates",
  invoice_hub: "finance",
  accounts_receivable: "finance",
  accounts_payable: "finance",
  finance_dashboard: "finance",
  finance_accounts: "finance",
  finance_transactions: "finance",
  finance_money_in: "finance",
  finance_money_out: "finance",
  finance_transfer: "finance",
  financial_position: "finance",
  intelligence: "intelligence",
  report_builder: "intelligence",
  project_analytics: "intelligence",
  payroll_home: "finance",
  payroll_queue: "finance",
  payroll_report: "intelligence",
  mission_control: "mission",
  play_store_readiness: "play_store_readiness",
  google_visibility: "google_visibility",
  settings: "settings",
  feedback: "feedback",
  feedback_inbox: "feedback_inbox",
});

export function isAppDesktopShellScreen(screenId) {
  return APP_DESKTOP_SHELL_SCREENS.has(screenId);
}

export function getAppDesktopNavActiveId(screenId) {
  return APP_DESKTOP_NAV_ACTIVE_BY_SCREEN[screenId] || null;
}

/** Visible name of the screen a caller-aware Back control returns to. */
export function appScreenReturnLabel(screenId) {
  const activeId = getAppDesktopNavActiveId(screenId);
  const item = APP_DESKTOP_NAV_ITEMS.find((entry) => entry.id === activeId);
  return item?.label || "Dashboard";
}

export function getVisibleAppDesktopNavItems(can, actor, orgPolicies, hostname) {
  const items = APP_DESKTOP_NAV_ITEMS.filter((item) => {
    if (item.sidebar === false) return false;
    if (item.nexoraOnly) {
      return actorCanAccessPlayStoreReadiness(actor);
    }
    if (item.surfaceRenaudOnly) {
      return actorCanAccessGoogleVisibility(actor);
    }
    if (item.id === "feedback") {
      return Boolean(actor) && !isGuestActor(actor);
    }
    if (item.id === "feedback_inbox") {
      return actorCanAccessFeedbackInbox(actor);
    }
    if (item.authenticated) {
      return Boolean(actor);
    }
    if (item.mode) {
      return canAccessMode(actor, item.mode, orgPolicies);
    }
    return can(item.capability);
  });
  if (isBranchMapPreviewHost(hostname)) {
    return [...items, GIT_BRANCH_MAP_NAV_ITEM];
  }
  return items;
}
